import React, { useCallback, useEffect, useState } from 'react';
import { View, Text, FlatList, StyleSheet, RefreshControl, TouchableOpacity, TextInput } from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import { theme } from '../../shared/theme/theme';
import { supabase } from '../../infrastructure/api/supabase';
import { useAuth } from '../../infrastructure/auth/AuthContext';
import { SupportAI } from '../../infrastructure/services/SupportAI';
import { EscrowService } from '../../domain/use-cases/EscrowService';
import { DisputeTrigger } from '../components/DisputeTrigger';
import { LiveTrackingMap } from '../components/LiveTrackingMap';
import { JobsRepository } from '../../infrastructure/repositories/JobsRepository';

interface JobRow {
  id: string;
  homee_id: string | null;
  status: 'PENDING' | 'ACTIVE' | 'DONE';
  escrow_status: 'HELD' | 'RELEASED' | 'DISPUTED';
  created_at: string;
  services: { segment: string; type: string; price_per_hour: number } | null;
  homee: { full_name: string; rating: number } | null;
}

const STATUS_LABEL: Record<JobRow['status'], string> = {
  PENDING: 'Čeká na match',
  ACTIVE: 'Probíhá',
  DONE: 'Dokončeno',
};

export const OrderHistoryScreen = () => {
  const { session } = useAuth();
  const [jobs, setJobs] = useState<JobRow[]>([]);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    if (!session) return;
    const { data, error } = await supabase
      .from('jobs')
      .select('id, homee_id, status, escrow_status, created_at, services(segment, type, price_per_hour), homee:homee_id(full_name, rating)')
      .eq('client_id', session.user.id)
      .order('created_at', { ascending: false });

    if (!error && data) setJobs(data as unknown as JobRow[]);
  }, [session]);

  useFocusEffect(useCallback(() => { load(); }, [load]));

  useFocusEffect(useCallback(() => {
    if (!session) return;
    const channel = supabase
      .channel(`jobs-live-${session.user.id}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'jobs', filter: `client_id=eq.${session.user.id}` }, () => load())
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [session, load]));

  const onRefresh = async () => {
    setRefreshing(true);
    await load();
    setRefreshing(false);
  };

  return (
    <View style={styles.container}>
      <Text style={styles.title}>Historie objednávek</Text>
      <FlatList
        data={jobs}
        keyExtractor={(item) => item.id}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
        contentContainerStyle={{ paddingBottom: 40 }}
        ListEmptyComponent={<Text style={styles.empty}>Zatím žádné objednávky.</Text>}
        renderItem={({ item }) => <JobCard job={item} onChanged={load} />}
      />
    </View>
  );
};

const JobCard: React.FC<{ job: JobRow; onChanged: () => void }> = ({ job, onChanged }) => {
  const [chatOpen, setChatOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [reply, setReply] = useState<string | null>(null);
  const [asking, setAsking] = useState(false);
  const [disputing, setDisputing] = useState(false);
  const [disputeMessage, setDisputeMessage] = useState<string | null>(null);

  const ask = async () => {
    if (!message.trim()) return;
    setAsking(true);
    const r = await SupportAI.handleSupportQuery(job.id, message);
    setReply(r);
    setAsking(false);
  };

  const dispute = async (reason: 'POOR_WORK' | 'UNFINISHED') => {
    const result = await EscrowService.triggerDispute(job.id, reason);
    setDisputeMessage(result.message);
    onChanged();
  };

  return (
    <View style={styles.card}>
      <Text style={styles.segment}>{job.services?.segment ?? '—'} · {job.services?.type ?? ''}</Text>
      <Text style={styles.status}>{STATUS_LABEL[job.status]}</Text>
      {job.homee && <Text style={styles.homee}>HoMee: {job.homee.full_name} ({job.homee.rating.toFixed(1)}★)</Text>}
      <Text style={styles.meta}>Escrow: {job.escrow_status} · {new Date(job.created_at).toLocaleString('cs-CZ')}</Text>

      {job.status === 'ACTIVE' && job.homee_id && <JobTracking jobId={job.id} homeeId={job.homee_id} />}

      <TouchableOpacity onPress={() => setChatOpen(!chatOpen)}>
        <Text style={styles.supportToggle}>{chatOpen ? 'Zavřít podporu' : 'Potřebujete pomoc?'}</Text>
      </TouchableOpacity>

      {chatOpen && (
        <View style={styles.supportBox}>
          {reply && <Text style={styles.supportReply}>{reply}</Text>}
          <View style={styles.supportRow}>
            <TextInput
              style={styles.supportInput}
              placeholder="Napište zprávu..."
              placeholderTextColor={theme.colors.text.muted}
              value={message}
              onChangeText={setMessage}
            />
            <TouchableOpacity style={styles.supportSend} onPress={ask} disabled={asking}>
              <Text style={styles.supportSendText}>{asking ? '...' : 'Odeslat'}</Text>
            </TouchableOpacity>
          </View>
        </View>
      )}

      {(job.status === 'ACTIVE' || job.status === 'DONE') && job.escrow_status === 'HELD' && (
        disputing
          ? <DisputeTrigger onDispute={dispute} />
          : (
            <TouchableOpacity onPress={() => setDisputing(true)}>
              <Text style={styles.supportToggle}>Něco není v pořádku?</Text>
            </TouchableOpacity>
          )
      )}

      {disputeMessage && <Text style={styles.supportReply}>{disputeMessage}</Text>}
    </View>
  );
};

interface Tracking {
  job_lat: number;
  job_lng: number;
  homee_lat: number | null;
  homee_lng: number | null;
  homee_name: string | null;
}

const JobTracking: React.FC<{ jobId: string; homeeId: string }> = ({ jobId, homeeId }) => {
  const [tracking, setTracking] = useState<Tracking | null>(null);

  const load = useCallback(async () => {
    const t = await JobsRepository.getTracking(jobId);
    if (t) setTracking(t as Tracking);
  }, [jobId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const channel = supabase
      .channel(`homee-position-${homeeId}`)
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'profiles', filter: `id=eq.${homeeId}` }, () => load())
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [homeeId, load]);

  if (!tracking) return null;

  return (
    <View style={styles.trackingBox}>
      <LiveTrackingMap
        jobLat={tracking.job_lat}
        jobLng={tracking.job_lng}
        homeeLat={tracking.homee_lat}
        homeeLng={tracking.homee_lng}
        homeeName={tracking.homee_name}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  trackingBox: {
    marginTop: 12,
  },
  container: {
    flex: 1,
    backgroundColor: theme.colors.background,
    padding: 24,
    paddingTop: 70,
  },
  title: {
    fontSize: 26,
    fontWeight: '900',
    color: theme.colors.text.main,
    marginBottom: 20,
  },
  empty: {
    color: theme.colors.text.muted,
    textAlign: 'center',
    marginTop: 40,
  },
  card: {
    backgroundColor: theme.colors.surface,
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: theme.colors.border,
  },
  segment: {
    fontWeight: '900',
    color: theme.colors.text.main,
    fontSize: 14,
    letterSpacing: 1,
  },
  status: {
    color: theme.colors.accent,
    fontWeight: '700',
    marginTop: 4,
  },
  homee: {
    color: theme.colors.text.main,
    marginTop: 4,
  },
  meta: {
    color: theme.colors.text.muted,
    fontSize: 11,
    marginTop: 6,
  },
  supportToggle: {
    color: theme.colors.accent,
    fontSize: 12,
    fontWeight: '700',
    marginTop: 12,
  },
  supportBox: {
    marginTop: 10,
    paddingTop: 10,
    borderTopWidth: 1,
    borderTopColor: theme.colors.border,
  },
  supportReply: {
    color: theme.colors.text.main,
    fontSize: 13,
    marginBottom: 10,
    fontStyle: 'italic',
  },
  supportRow: {
    flexDirection: 'row',
    gap: 8,
  },
  supportInput: {
    flex: 1,
    height: 40,
    borderWidth: 1,
    borderColor: theme.colors.border,
    borderRadius: 8,
    paddingHorizontal: 12,
    color: theme.colors.text.main,
  },
  supportSend: {
    height: 40,
    paddingHorizontal: 14,
    backgroundColor: theme.colors.accent,
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
  },
  supportSendText: {
    color: '#FFF',
    fontWeight: '700',
    fontSize: 12,
  },
});
